import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import VideocamIcon from "@mui/icons-material/Videocam";
import StopIcon from "@mui/icons-material/Stop";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import html2canvas from "html2canvas";
import { API_URL, getAuthHeaders } from "../../services/api/api";

type ReportType = "bug" | "feature";

interface FeedbackDialogProps {
  open: boolean;
  onClose: () => void;
}

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FeedbackDialog: React.FC<FeedbackDialogProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [reportType, setReportType] = useState<ReportType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");

  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordStartRef = useRef<number>(0);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successTaskId, setSuccessTaskId] = useState<string | null | undefined>(
    undefined
  );

  const captureScreenshot = useCallback(async () => {
    setCapturing(true);
    try {
      // Capture the page behind the dialog by rendering document.body.
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: window.devicePixelRatio > 1 ? 1.5 : 1,
        ignoreElements: (el) =>
          el.getAttribute?.("role") === "presentation" ||
          el.classList?.contains("MuiDialog-root") ||
          el.classList?.contains("MuiBackdrop-root"),
      });
      setScreenshot(canvas.toDataURL("image/png"));
    } catch (e) {
      // Screenshot is optional — fail silently but log for debugging.
      console.warn("[feedback] screenshot capture failed", e);
      setScreenshot(null);
    } finally {
      setCapturing(false);
    }
  }, []);

  // Auto-capture a screenshot shortly after the dialog opens.
  useEffect(() => {
    if (!open) return;
    // Reset transient state on open.
    setError(null);
    setSuccessTaskId(undefined);
    const t = setTimeout(() => {
      void captureScreenshot();
    }, 350);
    return () => clearTimeout(t);
  }, [open, captureScreenshot]);

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      mr.stop();
    }
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      chunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
        ? "video/webm;codecs=vp9"
        : "video/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;
      recordStartRef.current = Date.now();

      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        setVideoBlob(blob);
        setVideoDuration(
          Math.round((Date.now() - recordStartRef.current) / 1000)
        );
        setRecording(false);
      };
      // Stop automatically if the user ends the share via the browser UI.
      stream.getVideoTracks()[0]?.addEventListener("ended", stopRecording);

      recorder.start();
      setRecording(true);
    } catch (e: any) {
      // User cancelled the picker or permission denied — not an error state.
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") {
        console.warn("[feedback] screen recording failed", e);
      }
      setRecording(false);
    }
  }, [stopRecording]);

  const removeVideo = () => {
    setVideoBlob(null);
    setVideoDuration(0);
  };

  const resetAndClose = () => {
    if (submitting) return;
    if (recording) stopRecording();
    setReportType("bug");
    setTitle("");
    setDescription("");
    setScreenshot(null);
    setVideoBlob(null);
    setVideoDuration(0);
    setError(null);
    setSuccessTaskId(undefined);
    onClose();
  };

  const uploadMedia = async (
    dataUrl: string,
    filename: string
  ): Promise<string> => {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_URL}/feedback/media`, {
      method: "POST",
      headers,
      body: JSON.stringify({ dataUrl, filename }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Media upload failed (${res.status})`);
    }
    const body = await res.json();
    return body.url as string;
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError("Please enter a title.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const mediaUrls: string[] = [];

      if (screenshot) {
        mediaUrls.push(await uploadMedia(screenshot, "screenshot.png"));
      }
      if (videoBlob) {
        const videoDataUrl = await blobToDataUrl(videoBlob);
        mediaUrls.push(await uploadMedia(videoDataUrl, "recording.webm"));
      }

      const headers = await getAuthHeaders();
      const res = await fetch(`${API_URL}/feedback`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          reportType,
          title: title.trim(),
          description,
          mediaUrls,
          context: {
            url: window.location.href,
            userAgent: navigator.userAgent,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
          },
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `Submission failed (${res.status})`);
      }
      const body = await res.json();
      setSuccessTaskId(body.taskId ?? null);
    } catch (e: any) {
      setError(e?.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitted = successTaskId !== undefined;

  return (
    <Dialog
      open={open}
      onClose={resetAndClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: isDarkMode
            ? alpha(theme.palette.background.paper, 0.98)
            : "#ffffff",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1.5,
        }}
      >
        <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
          Send Feedback
        </Typography>
        <IconButton onClick={resetAndClose} size="small" disabled={submitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {submitted ? (
          <Stack spacing={2} sx={{ py: 2 }}>
            <Alert severity="success">
              Thanks! Your{" "}
              {reportType === "bug" ? "bug report" : "feature request"} was
              submitted
              {successTaskId ? ` (task ${successTaskId}).` : "."}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Our team will review it in Focus Forge.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              color="primary"
              value={reportType}
              onChange={(_e, val) => val && setReportType(val)}
              size="small"
            >
              <ToggleButton value="bug">
                <BugReportIcon fontSize="small" sx={{ mr: 1 }} />
                Bug
              </ToggleButton>
              <ToggleButton value="feature">
                <LightbulbIcon fontSize="small" sx={{ mr: 1 }} />
                Feature request
              </ToggleButton>
            </ToggleButtonGroup>

            <TextField
              label="Title"
              required
              fullWidth
              size="small"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={
                reportType === "bug"
                  ? "Briefly, what went wrong?"
                  : "Briefly, what would you like?"
              }
            />

            <TextField
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Add any detail that helps us understand and reproduce this."
            />

            {/* Screenshot */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Screenshot
              </Typography>
              <Box
                sx={{
                  border: `1px dashed ${alpha(theme.palette.divider, 0.6)}`,
                  borderRadius: 1.5,
                  p: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                {capturing ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ color: "text.secondary" }}
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2">Capturing…</Typography>
                  </Stack>
                ) : screenshot ? (
                  <>
                    <Box
                      component="img"
                      src={screenshot}
                      alt="Screenshot preview"
                      sx={{
                        width: 120,
                        height: 72,
                        objectFit: "cover",
                        borderRadius: 1,
                        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                      }}
                    />
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        startIcon={<CameraAltIcon />}
                        onClick={() => void captureScreenshot()}
                      >
                        Retake
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setScreenshot(null)}
                      >
                        Remove
                      </Button>
                    </Stack>
                  </>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      No screenshot attached.
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<CameraAltIcon />}
                      onClick={() => void captureScreenshot()}
                    >
                      Capture
                    </Button>
                  </Stack>
                )}
              </Box>
            </Box>

            {/* Screen recording */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Screen recording (optional)
              </Typography>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ flexWrap: "wrap" }}
              >
                {recording ? (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<StopIcon />}
                    onClick={stopRecording}
                  >
                    Stop recording
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VideocamIcon />}
                    onClick={() => void startRecording()}
                    disabled={submitting}
                  >
                    Record screen
                  </Button>
                )}
                {recording && (
                  <Typography variant="body2" color="error">
                    ● Recording…
                  </Typography>
                )}
                {!recording && videoBlob && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Attached — {videoDuration}s, {formatBytes(videoBlob.size)}
                    </Typography>
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={removeVideo}
                    >
                      Remove
                    </Button>
                  </Stack>
                )}
              </Stack>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {submitted ? (
          <Button variant="contained" onClick={resetAndClose}>
            Done
          </Button>
        ) : (
          <>
            <Button
              onClick={resetAndClose}
              color="inherit"
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => void handleSubmit()}
              disabled={submitting || !title.trim()}
              startIcon={
                submitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : null
              }
            >
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackDialog;
